import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import Select, { components } from 'react-select';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// If you have a toast system
import { toast } from 'react-hot-toast';

// Layout & UI components (adjust paths accordingly)
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Button from '../../shared/components/ui/Button';

// === Import your API functions (adjust paths) ===
import {
  getProjectByID,
  getDynamicFields,
  getDynamicFieldsProject,
  getDynamicFieldsUnit,
  getDynamicFieldsUnitPricing,
  getAnnexures,
  createTemplate
} from '../services/api/implementation/impl';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

// Modify the MultiValue component to include the delete button and proper dragging
const MultiValue = ({ children, ...props }) => {
  const userId = Cookies.get('userId');
  return (
    <components.MultiValue {...props}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#2A565E',
          borderRadius: '4px',
          padding: '2px 6px',
          color: 'white',
          cursor: 'grab'
        }}>
        {children}
      </div>
    </components.MultiValue>
  );
};

// Create a custom component for the value container
const ValueContainer = ({ children, ...props }) => {
  return (
    <components.ValueContainer {...props}>
      <DragDropContext
        onDragEnd={(result) => {
          if (!result.destination) return;

          const values = props.getValue();
          const newValues = Array.from(values);
          const [removed] = newValues.splice(result.source.index, 1);
          newValues.splice(result.destination.index, 0, removed);

          props.setValue(newValues);
        }}>
        <Droppable droppableId="droppable" direction="horizontal">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {React.Children.map(children, (child, index) => {
                if (child?.props?.data?.value) {
                  return (
                    <Draggable
                      key={child.props.data.value}
                      draggableId={child.props.data.value}
                      index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={{
                            ...provided.draggableProps.style,
                            opacity: snapshot.isDragging ? 0.8 : 1
                          }}>
                          {child}
                        </div>
                      )}
                    </Draggable>
                  );
                }
                return child;
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </components.ValueContainer>
  );
};

const NewDocumentTemplate = () => {
  const navigate = useNavigate();
  const { id: projectId } = useParams(); // e.g. /projects/:id

  // ====================== 1) Fetch Project (for company_id) ======================
  const {
    data: project,
    isLoading: isLoadingProject,
    isError: isErrorProject
  } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProjectByID(projectId),
    enabled: !!projectId
  });

  // ====================== 2) Fetch Dynamic Fields ======================
  // Returned data looks like:
  // [
  //   { id: 1, column_name: 'CLOSED_DEAL_ID', readable_name: 'Closed Deal Id' },
  //   ...
  // ]
  const {
    data: dynamicFieldsList,
    isLoading: isLoadingDynamicFields,
    isError: isErrorDynamicFields
  } = useQuery({
    queryKey: ['dynamic-fields'],
    queryFn: getDynamicFields // or pass projectId if needed
  });

  // ====================== 3) Fetch Annexures ======================
  // Example:
  // [
  //   { id: 1, column_name: "PROJECT_SPECIFICATION", readable_name: "Project Specification" },
  //   ...
  // ]
  const {
    data: annexureList,
    isLoading: isLoadingAnnexures,
    isError: isErrorAnnexures
  } = useQuery({
    queryKey: ['annexures'],
    queryFn: getAnnexures
  });

  const {
    data: projectDetailsFieldsList,
    isLoading: isLoadingProjectDetailsFields,
    isError: isErrorProjectDetailsFields
  } = useQuery({
    queryKey: ['dynamic-fields', 'project'],
    queryFn: getDynamicFieldsProject
  });

  const {
    data: unitDetailsFieldsList,
    isLoading: isLoadingUnitDetailsFields,
    isError: isErrorUnitDetailsFields
  } = useQuery({
    queryKey: ['dynamic-fields', 'unit'],
    queryFn: getDynamicFieldsUnit
  });

  const {
    data: unitPricingDetailsFieldsList,
    isLoading: isLoadingUnitPricingDetailsFields,
    isError: isErrorUnitPricingDetailsFields
  } = useQuery({
    queryKey: ['dynamic-fields', 'unit-pricing'],
    queryFn: getDynamicFieldsUnitPricing
  });

  // ====================== Component States ======================
  const [documentName, setDocumentName] = useState('');
  const [description, setDescription] = useState('');
  const [customDetails, setCustomDetails] = useState('');
  const [includeLogo, setIncludeLogo] = useState(true);
  const [includeWatermark, setIncludeWatermark] = useState(false);
  const { collapsed } = useSidebar();
  // Multi-select states
  const [selectedDynamicFields, setSelectedDynamicFields] = useState([]);
  const [selectedProjectDetails, setSelectedProjectDetails] = useState([]);
  const [selectedUnitDetails, setSelectedUnitDetails] = useState([]);
  const [selectedUnitPricingDetails, setSelectedUnitPricingDetails] = useState([]);
  const [selectedAnnexures, setSelectedAnnexures] = useState([]);

  // ====================== Build React-Select Options ======================
  //  We'll use `readable_name` as the label, and `column_name` as the value:
  //  e.g. { value: 'CLOSED_DEAL_ID', label: 'Closed Deal Id' }
  const dynamicFieldOptions = useMemo(() => {
    if (!dynamicFieldsList || !Array.isArray(dynamicFieldsList)) return [];
    return dynamicFieldsList.map((field) => ({
      value: field.column_name,
      label: field.readable_name
    }));
  }, [dynamicFieldsList]);

  const annexureOptions = useMemo(() => {
    if (!annexureList || !Array.isArray(annexureList)) return [];
    return annexureList.map((ann) => ({
      value: ann.column_name,
      label: ann.readable_name
    }));
  }, [annexureList]);

  const projectDetailsOptions = useMemo(() => {
    if (!projectDetailsFieldsList || !Array.isArray(projectDetailsFieldsList)) return [];
    return projectDetailsFieldsList.map((field) => ({
      value: field.column_name,
      label: field.readable_name
    }));
  }, [projectDetailsFieldsList]);

  const unitDetailsOptions = useMemo(() => {
    if (!unitDetailsFieldsList || !Array.isArray(unitDetailsFieldsList)) return [];
    return unitDetailsFieldsList.map((field) => ({
      value: field.column_name,
      label: field.readable_name
    }));
  }, [unitDetailsFieldsList]);

  const unitPricingDetailsOptions = useMemo(() => {
    if (!unitPricingDetailsFieldsList || !Array.isArray(unitPricingDetailsFieldsList)) return [];
    return unitPricingDetailsFieldsList.map((field) => ({
      value: field.column_name,
      label: field.readable_name
    }));
  }, [unitPricingDetailsFieldsList]);

  // ====================== 4) Create Template Mutation ======================
  const { mutate: mutateCreateTemplate, isLoading: isCreating } = useMutation({
    mutationFn: (payload) => createTemplate(payload),
    onSuccess: () => {
      toast.success('Template created successfully!');
      navigate(-1); // or wherever you want to go
    },
    onError: (error) => {
      console.error('Error creating template:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to create template');
    }
  });

  // ====================== Submit Handler ======================
  const handleSave = () => {
    // Ensure we have project data to get company_id
    if (!project) {
      toast.error('Project data not loaded yet.');
      return;
    }

    // Build payload with column_name
    const payload = {
      document_name: documentName,
      description: description,
      dynamic_fields: selectedDynamicFields.map((item) => item.value),
      project_dynamic_fields:
        selectedProjectDetails.length > 0
          ? selectedProjectDetails.map((item) => item.value)
          : null,
      unit_dynamic_fields:
        selectedUnitDetails.length > 0 ? selectedUnitDetails.map((item) => item.value) : null,
      unit_pricing_dynamic_fields:
        selectedUnitPricingDetails.length > 0
          ? selectedUnitPricingDetails.map((item) => item.value)
          : null,
      annexure: selectedAnnexures.map((item) => item.value),
      content: customDetails,
      project_id: Number(projectId),
      company_id: Number(project.company_id),
      include_logo: includeLogo,
      include_watermark: includeWatermark,
      html_template: ''
    };

    mutateCreateTemplate(payload);
  };

  // ====================== Cancel / Go Back ======================
  const handleCancel = () => {
    navigate(-1);
  };

  // ====================== Render ======================
  const isLoadingAll =
    isLoadingProject ||
    isLoadingDynamicFields ||
    isLoadingAnnexures ||
    isLoadingProjectDetailsFields ||
    isLoadingUnitDetailsFields ||
    isLoadingUnitPricingDetailsFields;
  const isErrorAll =
    isErrorProject ||
    isErrorDynamicFields ||
    isErrorAnnexures ||
    isErrorProjectDetailsFields ||
    isErrorUnitDetailsFields ||
    isErrorUnitPricingDetailsFields;

  if (isLoadingAll) {
    return <div>Loading ...</div>;
  }
  if (isErrorAll) {
    return <div>Error loading data. Please try again later.</div>;
  }

  return (
    <Layout>
      {/* If you have a custom Header */}
      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <Header data={{ name: 'Create New Template' }} />
      </div>

      <div
        className={`fixed z-[5] top-[6rem] bottom-0 flex justify-center overflow-hidden px-4 pb-4 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <div className="flex h-full min-h-0 w-full max-w-6xl flex-col rounded-lg border border-gray-200 bg-white shadow-sm">
          {/* Page header + actions (fixed above scroll area) */}
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
            <h2 className="text-xl font-semibold">New Document Template</h2>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancel} displayType="secondary">
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isCreating}>
                {isCreating ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>

          {/* Scrollable form body */}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4">
          {/* Document Details */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="font-semibold">Document Name</label>
              <input
                type="text"
                className="w-full p-2 border rounded mt-1"
                placeholder="Allotment Letter..."
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
              />
            </div>
            <div>
              <label className="font-semibold">Description</label>
              <input
                type="text"
                className="w-full p-2 border rounded mt-1"
                placeholder="Short description..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>

          <div className="mb-6 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
            <p className="text-sm font-semibold text-slate-700">PDF look</p>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-1"
                checked={includeLogo}
                onChange={(e) => setIncludeLogo(e.target.checked)}
              />
              <span>
                Company logo in header
                <span className="block text-xs text-slate-500">From company settings</span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-1"
                checked={includeWatermark}
                onChange={(e) => setIncludeWatermark(e.target.checked)}
              />
              <span>
                Watermark on body
                <span className="block text-xs text-slate-500">
                  Faded logo/name behind the agreement — optional
                </span>
              </span>
            </label>
          </div>

          {/* Dynamic Fields Multi-Select */}
          <div className="mb-6">
            <label className="font-semibold block mb-2">Dynamic Fields</label>
            <Select
              isMulti
              value={selectedDynamicFields}
              onChange={(newVal) => setSelectedDynamicFields(newVal)}
              options={dynamicFieldOptions}
              placeholder="Select dynamic fields..."
              components={{
                MultiValue,
                ValueContainer
              }}
              styles={{
                multiValue: (base) => ({
                  ...base,
                  backgroundColor: '#2A565E',
                  margin: '2px'
                }),
                multiValueLabel: (base) => ({
                  ...base,
                  color: 'white',
                  padding: '2px'
                }),
                multiValueRemove: (base) => ({
                  ...base,
                  color: 'black',
                  cursor: 'pointer',
                  ':hover': {
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    color: 'white'
                  }
                }),
                valueContainer: (base) => ({
                  ...base,
                  padding: '2px 8px',
                  gap: '2px'
                })
              }}
            />
          </div>

          {/* Project Details Multi-Select */}
          <div className="mb-6">
            <label className="font-semibold block mb-2">Project Details</label>
            <Select
              isMulti
              value={selectedProjectDetails}
              onChange={(newVal) => setSelectedProjectDetails(newVal)}
              options={projectDetailsOptions}
              placeholder="Select project detail fields..."
              components={{
                MultiValue,
                ValueContainer
              }}
              styles={{
                multiValue: (base) => ({
                  ...base,
                  backgroundColor: '#2A565E',
                  margin: '2px'
                }),
                multiValueLabel: (base) => ({
                  ...base,
                  color: 'white',
                  padding: '2px'
                }),
                multiValueRemove: (base) => ({
                  ...base,
                  color: 'black',
                  cursor: 'pointer',
                  ':hover': {
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    color: 'white'
                  }
                }),
                valueContainer: (base) => ({
                  ...base,
                  padding: '2px 8px',
                  gap: '2px'
                })
              }}
            />
          </div>

          {/* Unit Details Multi-Select */}
          <div className="mb-6">
            <label className="font-semibold block mb-2">Unit Details</label>
            <Select
              isMulti
              value={selectedUnitDetails}
              onChange={(newVal) => setSelectedUnitDetails(newVal)}
              options={unitDetailsOptions}
              placeholder="Select unit detail fields..."
              components={{
                MultiValue,
                ValueContainer
              }}
              styles={{
                multiValue: (base) => ({
                  ...base,
                  backgroundColor: '#2A565E',
                  margin: '2px'
                }),
                multiValueLabel: (base) => ({
                  ...base,
                  color: 'white',
                  padding: '2px'
                }),
                multiValueRemove: (base) => ({
                  ...base,
                  color: 'black',
                  cursor: 'pointer',
                  ':hover': {
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    color: 'white'
                  }
                }),
                valueContainer: (base) => ({
                  ...base,
                  padding: '2px 8px',
                  gap: '2px'
                })
              }}
            />
          </div>

          {/* Unit Pricing Details Multi-Select */}
          <div className="mb-6">
            <label className="font-semibold block mb-2">Unit Pricing Details</label>
            <Select
              isMulti
              value={selectedUnitPricingDetails}
              onChange={(newVal) => setSelectedUnitPricingDetails(newVal)}
              options={unitPricingDetailsOptions}
              placeholder="Select unit pricing fields..."
              components={{
                MultiValue,
                ValueContainer
              }}
              styles={{
                multiValue: (base) => ({
                  ...base,
                  backgroundColor: '#2A565E',
                  margin: '2px'
                }),
                multiValueLabel: (base) => ({
                  ...base,
                  color: 'white',
                  padding: '2px'
                }),
                multiValueRemove: (base) => ({
                  ...base,
                  color: 'black',
                  cursor: 'pointer',
                  ':hover': {
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    color: 'white'
                  }
                }),
                valueContainer: (base) => ({
                  ...base,
                  padding: '2px 8px',
                  gap: '2px'
                })
              }}
            />
          </div>

          {/* Annexure Multi-Select */}
          <div className="mb-6">
            <label className="font-semibold block mb-2">Annexure (Additional Docs)</label>
            <Select
              isMulti
              value={selectedAnnexures}
              onChange={(newVal) => setSelectedAnnexures(newVal)}
              options={annexureOptions}
              placeholder="Select annexure documents..."
              components={{
                MultiValue,
                ValueContainer
              }}
              styles={{
                multiValue: (base) => ({
                  ...base,
                  backgroundColor: '#2A565E',
                  margin: '2px'
                }),
                multiValueLabel: (base) => ({
                  ...base,
                  color: 'white',
                  padding: '2px'
                }),
                multiValueRemove: (base) => ({
                  ...base,
                  color: 'black',
                  cursor: 'pointer',
                  ':hover': {
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    color: 'white'
                  }
                }),
                valueContainer: (base) => ({
                  ...base,
                  padding: '2px 8px',
                  gap: '2px'
                })
              }}
            />
          </div>

          {/* Rich Text Editor for Template Body */}
          <div className="mb-6">
            <label className="font-semibold block mb-2">Custom Details (Body)</label>
            <ReactQuill
              theme="snow"
              value={customDetails}
              onChange={setCustomDetails}
              placeholder="Type or paste the main body of your template here..."
              className="bg-white"
            />
          </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default NewDocumentTemplate;
